import type { ZodType, ZodTypeDef } from 'zod/v3'
import { z } from 'zod/v3'

/**
 * Coerces \"true\"/\"false\" string values (and booleans) from search params to booleans for validation.
 */
export function zodBooleanFromSearchParam() {
  return z.preprocess(val => {
    if (val === 'true') return true
    if (val === 'false') return false
    return val
  }, z.boolean().optional())
}

/**
 * Creates a zod error map that can be used to customize the error message
 * for custom errors.
 */
const laterErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (issue.code === z.ZodIssueCode.custom) {
    if (!issue.params) {
      return { message: issue.message }
    }

    switch (issue.params.key) {
      case 'minimumCount': {
        return { message: issue.params.message ?? `Must have at least ${issue.params.count} characters.` }
      }
      case 'maximumCount': {
        return { message: issue.params.message ?? `Must have at most ${issue.params.count} characters.` }
      }
    }
  }

  return { message: ctx.defaultError }
}

z.setErrorMap(laterErrorMap)

/**
 * This converts an array of strings into a tuple so that it can be
 * passed to z.enum() to create a zod enum.
 */
export function zodEnumFromArray<T>(arr: readonly T[]) {
  const values: [T, ...T[]] = [arr[0], ...arr.slice(1)]
  return values
}

interface CreatePathValidationOptions {
  minimumCount: number
  maximumCount: number
  tooShortMessage?: (count: number, min: number) => string
  tooLongMessage?: (count: number, max: number) => string
}

/**
 * Creates a zod validation function that ensures the length of the input
 * is within the minimum and maximum counts.
 */
export function createLengthValidation<T extends ZodType<string | unknown[], ZodTypeDef, string | unknown[]>>(
  schema: T,
  options: CreatePathValidationOptions,
): z.ZodEffects<T> {
  const { minimumCount, maximumCount, tooShortMessage, tooLongMessage } = options

  return schema.superRefine((val, ctx) => {
    if (val.length < minimumCount) {
      ctx.addIssue({
        code: 'custom',
        params: {
          key: 'minimumCount',
          count: minimumCount,
          message: tooShortMessage ? tooShortMessage(val.length, minimumCount) : undefined,
        },
        message: tooShortMessage ? tooShortMessage(val.length, minimumCount) : undefined,
      })
    }

    if (val.length > maximumCount) {
      ctx.addIssue({
        code: 'custom',
        params: {
          key: 'maximumCount',
          count: maximumCount,
          message: tooLongMessage ? tooLongMessage(val.length, maximumCount) : undefined,
        },
        message: tooLongMessage ? tooLongMessage(val.length, maximumCount) : undefined,
      })
    }
  })
}
